/* Typed fetch client for the Phase 1 API.
 *
 * All requests go to `/api/*` with cookies included; in dev Vite proxies that to
 * the API (same-origin cookies), in production nginx does. The server is the
 * single source of truth — this client never computes game results. */
import type {
  SignupInput, LoginInput, CreateClassInput, JoinClassInput,
  CreateTeamInput, CreateGameInput, SubmitRoundInput, UpdatePreferencesInput,
  SetGradeInput, UpdateClassInput, SubmitQuizInput, SubmitJournalInput,
  LogActivityInput,
} from '@venturewrist/shared';
import type {
  PublicUser, ClassRow, TeamRow, GameSummary, GameDetail, AdvanceResponse, ClassOverview,
  JournalEntry, StudentReviewData,
} from './types';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error || res.statusText || 'Request failed';
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  // auth
  me: () => http<{ user: PublicUser }>('GET', '/auth/me'),
  signup: (b: SignupInput) => http<{ user: PublicUser }>('POST', '/auth/signup', b),
  login: (b: LoginInput) => http<{ user: PublicUser }>('POST', '/auth/login', b),
  logout: () => http<{ ok: true }>('POST', '/auth/logout', {}),
  updatePreferences: (b: UpdatePreferencesInput) => http<{ user: PublicUser }>('PATCH', '/auth/me', b),
  logActivity: (b: LogActivityInput) => http<{ ok: true }>('POST', '/activity', b),

  // classes
  listClasses: () => http<{ classes: ClassRow[] }>('GET', '/classes'),
  getClass: (id: string) => http<{ class: ClassRow; role: string }>('GET', `/classes/${id}`),
  createClass: (b: CreateClassInput) => http<{ class: ClassRow }>('POST', '/classes', b),
  joinClass: (b: JoinClassInput) => http<{ class: ClassRow }>('POST', '/classes/join', b),
  listTeams: (classId: string) => http<{ teams: TeamRow[] }>('GET', `/classes/${classId}/teams`),
  classOverview: (id: string) => http<ClassOverview>('GET', `/classes/${id}/overview`),
  setGrade: (id: string, userId: string, b: SetGradeInput) => http<{ grade: unknown }>('PUT', `/classes/${id}/grades/${userId}`, b),
  updateClass: (id: string, b: UpdateClassInput) => http<{ class: ClassRow }>('PATCH', `/classes/${id}`, b),

  // assessment & roster support
  reviewStudent: (classId: string, userId: string) => http<StudentReviewData>('GET', `/classes/${classId}/students/${userId}`),
  resetStudentPassword: (classId: string, userId: string) =>
    http<{ tempPassword: string }>('POST', `/classes/${classId}/students/${userId}/reset-password`, {}),
  submitQuiz: (classId: string, b: SubmitQuizInput) => http<{ quiz: unknown }>('POST', `/classes/${classId}/quiz`, b),
  submitJournal: (classId: string, b: SubmitJournalInput) => http<{ journal: JournalEntry }>('POST', `/classes/${classId}/journal`, b),
  myJournals: (classId: string) => http<{ journals: JournalEntry[] }>('GET', `/classes/${classId}/journals`),

  // teams
  getTeam: (id: string) => http<{ team: TeamRow; games: GameSummary[] }>('GET', `/teams/${id}`),
  createTeam: (b: CreateTeamInput) => http<{ team: TeamRow }>('POST', '/teams', b),
  joinTeam: (id: string) => http<{ team: TeamRow }>('POST', `/teams/${id}/join`, {}),

  // games
  createGame: (b: CreateGameInput) => http<{ game: GameSummary }>('POST', '/games', b),
  getGame: (id: string) => http<GameDetail>('GET', `/games/${id}`),
  advanceGame: (id: string, b: SubmitRoundInput) =>
    http<AdvanceResponse>('POST', `/games/${id}/advance`, b),
};
